import React, { useState, useMemo } from 'react';
import { SpreadsheetSection } from './spreadsheet-section';
import { useSpreadsheetNavigation } from './use-spreadsheet-navigation';
import type { SpreadsheetConfig } from './types';

/**
 * FinancialSpreadsheet Component
 *
 * Main container for the spreadsheet with:
 * - Multi-period column layout
 * - Simplified/Detailed mode toggle
 * - Collapsible sections
 * - Keyboard navigation
 * - Read-only mode
 */
export const FinancialSpreadsheet: React.FC<SpreadsheetConfig> = ({
  sections,
  periods,
  onCellChange,
  mode,
  readOnly = false,
  onAddRow,
  onDeleteRow
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(sections.map((s) => s.id))
  );

  /**
   * Filter sections based on mode
   */
  const visibleSections = useMemo(() => {
    if (mode === 'simplified') {
      // In simplified mode, only show summary rows from each section
      return sections.map((section) => ({
        ...section,
        rows: section.rows.filter((row) => row.type === 'total' || row.type === 'subtotal')
      }));
    }

    return sections;
  }, [sections, mode]);

  /**
   * Calculate total rows for navigation
   */
  const totalRows = useMemo(() => {
    return visibleSections.reduce((count, section) => {
      const sectionRows = section.rows.length + (section.totalRow ? 1 : 0);
      return count + sectionRows;
    }, 0);
  }, [visibleSections]);

  /**
   * Keyboard navigation hook
   */
  const { focusedCell, navigate } = useSpreadsheetNavigation({
    totalRows,
    totalColumns: periods.length
  });

  /**
   * Toggle section expansion
   */
  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  /**
   * Calculate row start index for each section
   */
  const getSectionRowStartIndex = (sectionIndex: number): number => {
    return visibleSections.slice(0, sectionIndex).reduce((count, section) => {
      return count + section.rows.length + (section.totalRow ? 1 : 0);
    }, 0);
  };

  return (
    <div className="financial-spreadsheet">
      {/* Header row */}
      <div className="mb-4 grid grid-cols-[280px_repeat(auto-fit,_minmax(140px,_1fr))] bg-gray-50 border border-gray-300">
        {/* Empty cell for row labels */}
        <div className="px-3 py-2 font-semibold border-r border-gray-300" />

        {/* Period headers */}
        {periods.map((period) => (
          <div
            key={period.periodId}
            className="px-3 py-2 text-center border-r border-gray-300"
            data-testid={`spreadsheet-header-${period.periodId}${period.isAnnualised ? '-annualised' : ''}`}
          >
            <div className="font-semibold text-sm">
              {period.label}
              {period.isAnnualised && <span className="text-xs ml-1">(ann.)</span>}
            </div>

            {/* Trust grade badge */}
            <div
              className={`text-xs mt-1 inline-block px-2 py-0.5 rounded ${
                period.trustGrade === 'credit_grade'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-blue-100 text-blue-800'
              }`}
              data-testid={`trust-grade-badge-${period.periodId}`}
            >
              {period.trustGrade === 'credit_grade' ? 'Credit' : 'Insight'}
            </div>
          </div>
        ))}
      </div>

      {/* Sections */}
      {visibleSections.map((section, index) => (
        <SpreadsheetSection
          key={section.id}
          section={section}
          periods={periods}
          onCellChange={onCellChange}
          readOnly={readOnly}
          expanded={expandedSections.has(section.id)}
          onToggle={() => toggleSection(section.id)}
          focusedCell={focusedCell}
          rowStartIndex={getSectionRowStartIndex(index)}
          onNavigate={navigate}
          onAddRow={onAddRow}
          onDeleteRow={onDeleteRow}
        />
      ))}
    </div>
  );
};
